import React from "react";
import styled, { css } from "styled-components";
import { FormattedNumber } from "@/i18n/i18n-client";
import { LeftSmallArrow } from "@/assets/Icons/LeftSmallArrow";
import { RightSmallArrow } from "@/assets/Icons/RightSmallArrow";
import { rtl, themeColor } from "@/shared/styles-utils";
import { Link, usePathname, useSearchParams } from "@/i18n/i18n-navigation";

const StyledPagination = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 20px 0 110px;
`;

const PagesLinks = styled.div<{
  $rounded: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  ${({ $rounded }) => $rounded && "border-radius: 999px;"}
`;

const PageLink = styled(Link)<{ $active: boolean }>`
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  padding: 12px;
  text-align: center;
  color: ${({ theme }) => theme.text.alt};
  &:not(:last-child) {
    /* margin-right: 31px; */
  }
  ${({ $active }) =>
    $active
      ? css`
          color: ${themeColor("primary")};
        `
      : ""}
`;

const Navigate = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.special.border};
  background-color: ${themeColor("primary")};
  cursor: pointer;
  color: #fff;

  svg {
    transform: scaleX(${rtl("-1", "1")});
  }

  &:visited {
    color: #fff;
  }
`;

const PreviousLink = styled(Navigate)`
  ${rtl("margin-left", "margin-right")}: 30px;
  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      filter: opacity(50%);
    `}
`;

const NextLink = styled(Navigate)`
  ${rtl("margin-right", "margin-left")}: 30px;
  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      filter: opacity(50%);
    `}
`;

function setSearchParams(searchParams: URLSearchParams, page: number | string) {
  const params = new URLSearchParams(searchParams.toString());
  params.set("page", String(page));
  return params.toString();
}

interface PaginationProps {
  offset: number;
  totalCount: number;
  itemPerPage: number;
  style?: React.CSSProperties;
  rounded?: boolean;
}

export const Pagination = ({
  offset,
  totalCount,
  itemPerPage,
  style,
  rounded = false,
}: PaginationProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pagesCount = Math.ceil(totalCount / itemPerPage);
  const pagesArray = Array.from({ length: pagesCount }, (_, i) => i + 1);
  const currentPage = Math.floor(offset / itemPerPage) + 1;

  const [printedPages, setPrintedPages] =
    React.useState<(string | number)[]>(pagesArray);

  React.useEffect(() => {
    if (pagesCount > 5) {
      if (currentPage - 2 > 0 && currentPage + 2 < pagesCount) {
        setPrintedPages([
          1,
          currentPage - 2 + "",
          ...pagesArray.slice(currentPage - 2, currentPage + 1),
          currentPage + 2 + "",
          pagesCount,
        ]);
      } else if (currentPage - 2 <= 0) {
        setPrintedPages([
          ...pagesArray.slice(0, 4),
          pagesArray[5] + "",
          pagesCount,
        ]);
      } else if (currentPage + 2 >= pagesCount) {
        setPrintedPages([
          1,
          pagesArray[pagesCount - 5] + "",
          ...pagesArray.slice(pagesCount - 4),
        ]);
      }
    } else {
      setPrintedPages(pagesArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pagesCount]);

  const pages = printedPages.map((page, index) => {
    const queries = setSearchParams(searchParams, page);
    return (
      <PageLink
        key={`page-${index}`}
        href={`${pathname}?${queries}`}
        $active={page === currentPage}
      >
        {typeof page === "string" ? "..." : <FormattedNumber value={page} />}
      </PageLink>
    );
  });

  const previousPage = currentPage - 1 < 1 ? 1 : currentPage - 1;
  const previousQueries = setSearchParams(searchParams, previousPage);

  const nextPage = currentPage + 1;
  const nextQueries = setSearchParams(searchParams, nextPage);

  if (totalCount <= itemPerPage) {
    return null;
  }

  return (
    <StyledPagination style={style}>
      <PreviousLink
        title="Previous"
        href={`${pathname}?${previousQueries}`}
        disabled={currentPage <= 1}
      >
        <LeftSmallArrow />
      </PreviousLink>
      <PagesLinks $rounded={rounded}>{pages}</PagesLinks>
      <NextLink
        title="Next"
        href={`${pathname}?${nextQueries}`}
        disabled={currentPage === pagesCount}
      >
        <RightSmallArrow />
      </NextLink>
    </StyledPagination>
  );
};
